import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { eur } from "@/lib/format";

const fieldCls = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all";

export const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2));
export const withKey = (item) => ({ ...item, _k: newKey() });

export function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl p-6">
      <h3 className="font-display font-semibold text-base mb-4">{title}</h3>
      {children}
    </div>
  );
}

export function Label({ children }) {
  return <span className="block text-xs font-medium text-slate-600 mb-1">{children}</span>;
}

export function Input({ label, testid, type = "text", value, onChange, placeholder }) {
  return (
    <label className="block">
      {label ? <Label>{label}</Label> : null}
      <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} step={type === "number" ? "any" : undefined} className={fieldCls} />
    </label>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-slate-400" : "text-slate-200"}>{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

const CUSTOMER_FIELDS = [
  ["company_name", "Firma", "cust-company"],
  ["contact", "Ansprechpartner", "cust-contact"],
  ["street", "Straße & Nr.", "cust-street"],
];

export function CustomerCard({ customer, setCustomer, customers, onPick }) {
  const set = (k) => (v) => setCustomer({ ...customer, [k]: v });
  return (
    <Card title="Empfänger">
      <div className="mb-4">
        <Label>Aus Kundendatenbank übernehmen</Label>
        <select onChange={(e) => onPick(e.target.value)} data-testid="customer-select" className={fieldCls} defaultValue="">
          <option value="">— Kunde auswählen —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{`${c.company_name || c.contact}${c.city ? " · " + c.city : ""}`}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CUSTOMER_FIELDS.map(([k, label, tid]) => (
          <Input key={k} label={label} testid={tid} value={customer[k]} onChange={set(k)} />
        ))}
        <div className="grid grid-cols-3 gap-3">
          <Input label="PLZ" testid="cust-zip" value={customer.zip} onChange={set("zip")} />
          <div className="col-span-2"><Input label="Ort" testid="cust-city" value={customer.city} onChange={set("city")} /></div>
        </div>
        <Input label="E-Mail" testid="cust-email" value={customer.email} onChange={set("email")} />
        <Input label="USt-IdNr." testid="cust-vat" value={customer.vat_id} onChange={set("vat_id")} />
      </div>
    </Card>
  );
}

export function DatesCard({ f, set, placeholder }) {
  return (
    <Card title="Daten">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input label="Rechnungsdatum" type="date" testid="invoice-date" value={f.invoiceDate} onChange={set("invoiceDate")} />
        <Input label={f.serviceDateEnd ? "Leistung von" : "Leistungsdatum"} type="date" testid="service-date" value={f.serviceDate} onChange={set("serviceDate")} />
        <Input label="Leistung bis (optional)" type="date" testid="service-date-end" value={f.serviceDateEnd} onChange={set("serviceDateEnd")} />
        <Input label="Fällig am" type="date" testid="due-date" value={f.dueDate} onChange={set("dueDate")} />
      </div>
      {(f.refLabel || f.reference) ? (
        <div className="mt-3">
          <Input label={f.refLabel || "Referenz"} testid="reference-input" value={f.reference} onChange={set("reference")} placeholder={placeholder} />
        </div>
      ) : (
        <button type="button" onClick={() => set("refLabel")("Referenz")} data-testid="add-reference-button" className="mt-3 text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4">
          + Aktenzeichen / Referenz hinzufügen
        </button>
      )}
    </Card>
  );
}

function ItemRow({ it, idx, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-end" data-testid={`item-row-${idx}`}>
      <div className="col-span-12 sm:col-span-6">
        <Input label={idx === 0 ? "Leistungsbeschreibung" : ""} testid={`item-desc-${idx}`} value={it.description} onChange={(v) => onChange("description", v)} placeholder="z.B. Beratungsleistung" />
      </div>
      <div className="col-span-4 sm:col-span-2">
        <Input label={idx === 0 ? "Menge" : ""} type="number" testid={`item-qty-${idx}`} value={it.quantity} onChange={(v) => onChange("quantity", v)} />
      </div>
      <div className="col-span-5 sm:col-span-3">
        <Input label={idx === 0 ? "Einzelpreis (€)" : ""} type="number" testid={`item-price-${idx}`} value={it.unit_price} onChange={(v) => onChange("unit_price", v)} />
      </div>
      <div className="col-span-3 sm:col-span-1 flex justify-end">
        <button onClick={onRemove} data-testid={`remove-item-${idx}`} className="h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function ItemsCard({ items, setItems, extras = [] }) {
  const upd = (key, k, v) => setItems(items.map((it) => (it._k === key ? { ...it, [k]: v } : it)));
  const remove = (key) => setItems(items.length === 1 ? items : items.filter((it) => it._k !== key));
  const add = (item) => setItems([...items, withKey(item)]);
  return (
    <Card title="Positionen">
      <div className="space-y-3">
        {items.map((it, idx) => (
          <ItemRow key={it._k} it={it} idx={idx} onChange={(k, v) => upd(it._k, k, v)} onRemove={() => remove(it._k)} />
        ))}
      </div>
      <button onClick={() => add({ description: "", quantity: 1, unit_price: 0 })} data-testid="add-item-button" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 border border-dashed border-slate-300 hover:border-slate-400 rounded-lg px-3.5 py-2 transition-colors">
        <Plus className="h-4 w-4" /> Position hinzufügen
      </button>
      {extras.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="extras-row">
          <p className="text-xs font-medium text-slate-500 mb-2">Weitere Positionen hinzufügen</p>
          <div className="flex flex-wrap gap-2">
            {extras.map((x) => (
              <button key={x.key} onClick={() => add(x.item)} data-testid={`add-extra-${x.key}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 border border-dashed border-blue-300 hover:border-blue-400 rounded-lg px-3 py-1.5 transition-colors">
                <Plus className="h-3.5 w-3.5" /> {x.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export function VatCard({ f, set }) {
  return (
    <Card title="MwSt & Rechnungstext">
      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input type="checkbox" checked={f.klein} onChange={(e) => set("klein")(e.target.checked)} data-testid="kleinunternehmer-toggle" className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30" />
        <span className="text-sm text-slate-700">Kleinunternehmerregelung nach § 19 UStG (keine MwSt ausweisen)</span>
      </label>
      {!f.klein && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <Label>MwSt-Satz</Label>
            <div className="flex gap-2">
              {[19, 7, 0].map((r) => (
                <button key={r} onClick={() => set("vatRate")(r)} data-testid={`vat-preset-${r}`} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${Number(f.vatRate) === r ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {r} %
                </button>
              ))}
              <input type="number" value={f.vatRate} onChange={(e) => set("vatRate")(e.target.value)} data-testid="vat-custom-input" className="w-20 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" title="Individueller Satz" />
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <select value={f.status} onChange={(e) => set("status")(e.target.value)} data-testid="status-select" className={fieldCls}>
              <option value="entwurf">Entwurf</option>
              <option value="offen">Offen</option>
              <option value="bezahlt">Bezahlt</option>
            </select>
          </div>
        </div>
      )}
      <div>
        <Label>Anmerkung / Zahlungstext</Label>
        <textarea value={f.note} onChange={(e) => set("note")(e.target.value)} rows={3} data-testid="note-input" className={fieldCls} placeholder="z.B. Zahlbar innerhalb von 14 Tagen ohne Abzug." />
      </div>
    </Card>
  );
}

export function SummaryCard({ net, vatAmount, gross, klein, vatRate }) {
  return (
    <div className="bg-blue-700 text-white rounded-2xl p-6 space-y-4" data-testid="totals-summary">
      <h3 className="font-display font-semibold text-lg">Zusammenfassung</h3>
      <div className="space-y-2.5 text-sm">
        <Row label="Netto" value={eur(net)} />
        <Row label={klein ? "MwSt (0 % · § 19)" : `MwSt (${vatRate} %)`} value={eur(vatAmount)} muted />
        <div className="h-px bg-white/10 my-1" />
        <div className="flex items-center justify-between">
          <span className="font-medium">Brutto</span>
          <span className="font-mono tabular-nums text-xl font-bold" data-testid="gross-total">{eur(gross)}</span>
        </div>
      </div>
      {klein && <p className="text-xs text-slate-400 leading-relaxed border-t border-white/10 pt-3">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>}
    </div>
  );
}
